import { useEffect, useMemo, useRef, useState } from 'react';
import { reconcileDeck, discoveryReturnBudget } from './stableDiscoveryDeck';
import { retainRecommendedTrackWindow } from './selectRecommendedTrackWindow';
import { readDiscoveryState, writeDiscoveryState, trackReturnRounds } from './storage';
const EMPTY = [];
const localDay = () => {
  const date = new Date();
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
};
const counter = (value) => (Number.isSafeInteger(value) && value >= 0 ? value : 0);
function albumClock(previous) {
  const albumDate = localDay();
  return {
    albumDate,
    albumRotation: previous.albumDate === albumDate ? counter(previous.albumRotation) : 0,
    manualRefreshCount:
      previous.albumDate === albumDate ? counter(previous.manualRefreshCount) % 2 : 0,
  };
}
// The caller is keyed by account. No guest/member deck can leak into another scope.
export function useHomeSelection(scope, data, onLongReturn) {
  const [state, setState] = useState(() => readDiscoveryState(scope));
  const current = useRef(state);
  const returned = useRef(onLongReturn);
  returned.current = onLongReturn;
  const [visit, setVisit] = useState(0);
  const albums = useMemo(
    () =>
      (data?.albums || EMPTY).filter(
        (item) => !(data?.excludedAlbumIds || EMPTY).includes(item.id),
      ),
    [data],
  );
  const tracks = useMemo(
    () =>
      (data?.tracks || EMPTY).filter(
        (item) =>
          !(data?.excludedTrackIds || EMPTY).includes(item.id) &&
          !(data?.excludedAlbumIds || EMPTY).includes(item.albumId),
      ),
    [data],
  );
  const memberIds = useMemo(
    () =>
      albums
        .filter((item) => ['SIMILAR_LISTENERS', 'CO_LIKED_ALBUM'].includes(item.reason))
        .map((item) => item.id),
    [albums],
  );
  const save = (next) => {
    current.current = next;
    setState(next);
    writeDiscoveryState(scope, next);
  };
  useEffect(() => {
    if (!data || document.hidden) return;
    const previous = current.current;
    const elapsed = previous.awayAt == null ? 0 : Math.max(0, Date.now() - previous.awayAt);
    const rotation = previous.rotation + trackReturnRounds(elapsed);
    const repeated =
      previous.deck.ids.filter(
        (id) =>
          (previous.deck.seen[id] || 0) >= 2 &&
          Date.now() - (previous.deck.introduced[id] || 0) >= 30 * 60000,
      ).length >= 2;
    const clock = albumClock(previous);
    if (elapsed >= 30 * 60000) {
      clock.albumRotation++;
      clock.manualRefreshCount = 0;
    }
    const deck = reconcileDeck(
      previous.deck,
      albums.map((item) => item.id),
      `${clock.albumDate}:${clock.albumRotation}`,
      'auto',
      Date.now(),
      discoveryReturnBudget(elapsed, repeated),
      memberIds,
    );
    const selectedTracks = retainRecommendedTrackWindow(tracks, rotation, {
      rotation: previous.rotation,
      tracks: previous.tracks.filter(
        (track) =>
          !(data.excludedTrackIds || []).includes(track.id) &&
          !(data.excludedAlbumIds || []).includes(track.albumId),
      ),
    });
    const next = { ...previous, ...clock, deck, tracks: selectedTracks, rotation, awayAt: null };
    current.current = next;
    setState(next);
    writeDiscoveryState(scope, next);
    if (elapsed >= 30 * 60000) returned.current?.();
  }, [scope, albums, tracks, memberIds, visit, data]);
  useEffect(() => {
    const depart = () => {
      // hidden, pagehide and unmount can all fire for the same departure.
      const next = { ...current.current, awayAt: current.current.awayAt ?? Date.now() };
      current.current = next;
      writeDiscoveryState(scope, next);
    };
    const visibility = () => {
      if (document.hidden) depart();
      else setVisit((value) => value + 1);
    };
    document.addEventListener('visibilitychange', visibility);
    window.addEventListener('pagehide', depart);
    return () => {
      depart();
      document.removeEventListener('visibilitychange', visibility);
      window.removeEventListener('pagehide', depart);
    };
  }, [scope]);
  const byId = new Map(albums.map((item) => [item.id, item]));
  return {
    albums: state.deck.ids.flatMap((id) => (byId.has(id) ? [byId.get(id)] : [])),
    tracks: state.tracks,
    rotation: state.rotation,
    albumRotation: state.albumRotation || 0,
    albumRotationKey: `${state.albumDate || localDay()}:${state.albumRotation || 0}`,
    advance: () => {
      if (!data) return;
      const previous = current.current;
      const clock = albumClock(previous);
      const rotateAlbums = clock.manualRefreshCount === 1;
      clock.manualRefreshCount = rotateAlbums ? 0 : 1;
      if (rotateAlbums) clock.albumRotation++;
      const rotation = previous.rotation + 1;
      save({
        ...previous,
        ...clock,
        rotation,
        awayAt: null,
        tracks: retainRecommendedTrackWindow(tracks, rotation, previous),
        deck: reconcileDeck(
          previous.deck,
          albums.map((item) => item.id),
          `${clock.albumDate}:${clock.albumRotation}`,
          rotateAlbums ? 'manual' : 'auto',
          Date.now(),
          rotateAlbums ? 2 : 0,
          memberIds,
        ),
      });
    },
    mark: (id, kind) => {
      const previous = current.current;
      if (!previous.deck.ids.includes(id)) return;
      const next = {
        ...previous,
        deck: {
          ...previous.deck,
          [kind]: { ...previous.deck[kind], [id]: (previous.deck[kind][id] || 0) + 1 },
        },
      };
      current.current = next;
      writeDiscoveryState(scope, next);
    },
    restoreTrack: (item, index = 0) => {
      const tracks = current.current.tracks.filter(
        (t) => t.id !== item.id && t.albumId !== item.albumId,
      );
      tracks.splice(Math.max(0, Math.min(index, tracks.length)), 0, item);
      save({ ...current.current, tracks: tracks.slice(0, 3) });
    },
    removeTrack: (id) =>
      save({ ...current.current, tracks: current.current.tracks.filter((item) => item.id !== id) }),
  };
}
